/**
 * @module SharedConfig
 * @package @geenius/i18n
 * @description Implements shared configuration builders and namespace-loading
 * utilities for Geenius i18n. These helpers define the runtime configuration
 * contract used by the shared layer, framework bindings, and Convex helpers.
 */

import {
  DEFAULT_CACHE_DURATION_MS,
  DEFAULT_TRANSLATIONS_BASE_URL,
  SUPPORTED_LOCALES,
} from "./constants";
import { I18nConfigError, I18nError, TranslationLoadError } from "./errors";
import type {
  I18nConfig,
  I18nNamespace,
  Locale,
  TranslationDict,
} from "./types";
import {
  boundedTranslationDictSchema,
  i18nConfigSchema,
  localeSchema,
  safeI18nNamespaceSchema,
  translationLoaderConfigSchema,
} from "./validators";

function mergeTranslationDicts(
  target: TranslationDict,
  source: TranslationDict,
): TranslationDict {
  for (const [key, value] of Object.entries(source)) {
    const existing = target[key];
    if (
      typeof value === "object" &&
      value !== null &&
      typeof existing === "object" &&
      existing !== null
    ) {
      target[key] = mergeTranslationDicts({ ...existing }, value);
    } else {
      target[key] = value;
    }
  }

  return target;
}

/**
 * Snapshot of the in-memory translation loader cache.
 */
export interface TranslationLoaderCacheStats {
  size: number;
  entries: string[];
}

/**
 * Async translation namespace loader with cache and preload controls.
 */
export interface TranslationLoader {
  load: (locale: Locale, namespace: I18nNamespace) => Promise<TranslationDict>;
  preload: (locales: Locale[], namespace: I18nNamespace) => Promise<void>;
  clearCache: (locale?: Locale, namespace?: I18nNamespace) => void;
  getCacheStats: () => TranslationLoaderCacheStats;
}

/**
 * In-memory namespace aggregator for loading, retrieving, and merging catalogs.
 */
export interface NamespaceLoader {
  load: (locale: Locale, namespace: I18nNamespace) => Promise<TranslationDict>;
  loadMultiple: (
    locale: Locale,
    namespaceList: I18nNamespace[],
  ) => Promise<Record<string, TranslationDict>>;
  get: (
    locale: Locale,
    namespace: I18nNamespace,
  ) => TranslationDict | undefined;
  merge: (locale: Locale, namespaceList: I18nNamespace[]) => TranslationDict;
}

const MULTI_PRESET_LOCALES = ["en", "fr", "de", "es", "it"] satisfies Locale[];

function includeDefaultLocale(
  locales: readonly Locale[],
  defaultLocale: Locale,
): Locale[] {
  if (locales.includes(defaultLocale)) {
    return [...locales];
  }

  return [defaultLocale, ...locales];
}

function normalizeLoaderRequest(
  locale: Locale,
  namespace: I18nNamespace,
): { locale: Locale; namespace: I18nNamespace } {
  const localeResult = localeSchema.safeParse(locale);
  const namespaceResult = safeI18nNamespaceSchema.safeParse(namespace);

  if (!localeResult.success || !namespaceResult.success) {
    throw new TranslationLoadError(
      String(locale),
      String(namespace) as I18nNamespace,
      new I18nConfigError("Invalid translation loader locale or namespace", {
        locale,
        namespace,
        localeIssues: localeResult.success
          ? []
          : localeResult.error.issues.map((issue) => issue.message),
        namespaceIssues: namespaceResult.success
          ? []
          : namespaceResult.error.issues.map((issue) => issue.message),
      }),
    );
  }

  return {
    locale: localeResult.data,
    namespace: namespaceResult.data,
  };
}

function buildNamespaceUrl(
  baseUrl: string,
  locale: Locale,
  namespace: I18nNamespace,
): string {
  const base = baseUrl.replace(/\/+$/, "");
  return `${base}/${encodeURIComponent(locale)}/${encodeURIComponent(namespace)}.json`;
}

/**
 * Fluent builder for Geenius i18n runtime configuration objects.
 *
 * Use this builder when an application needs to compose locale support,
 * fallback behavior, and developer diagnostics incrementally before freezing
 * the final runtime config.
 *
 * @example
 * ```ts
 * const i18nConfig = createI18nConfig()
 *   .withDefaultLocale("en")
 *   .withSupportedLocales(["en", "fr", "de"])
 *   .withFallbackLocale("en")
 *   .withNamespace("common")
 *   .build()
 * ```
 */
export class I18nConfigBuilder {
  private config: I18nConfig = {
    defaultLocale: "en",
    supportedLocales: ["en"],
    fallbackLocale: "en",
    namespace: "translations",
    debug: false,
  };

  /**
   * Sets the default locale used when no persisted or detected locale exists.
   *
   * @param locale Locale that should become the runtime default.
   * @returns The same builder instance for fluent chaining.
   */
  withDefaultLocale(locale: Locale): this {
    this.config.defaultLocale = locale;
    return this;
  }

  /**
   * Replaces the supported locale catalog for the current config draft.
   *
   * @param locales Locale codes that the application should allow at runtime.
   * @returns The same builder instance for fluent chaining.
   */
  withSupportedLocales(locales: Locale[]): this {
    this.config.supportedLocales = locales;
    return this;
  }

  /**
   * Sets the fallback locale used when a translation cannot be resolved.
   *
   * @param locale Locale used as the fallback translation source.
   * @returns The same builder instance for fluent chaining.
   */
  withFallbackLocale(locale: Locale): this {
    this.config.fallbackLocale = locale;
    return this;
  }

  /**
   * Sets the active default namespace for namespace-aware consumers.
   *
   * @param namespace Namespace identifier to associate with the config.
   * @returns The same builder instance for fluent chaining.
   */
  withNamespace(namespace: I18nNamespace): this {
    this.config.namespace = namespace;
    return this;
  }

  /**
   * Enables or disables verbose debug behavior in the runtime config.
   *
   * @param enabled Whether debug diagnostics should be enabled.
   * @returns The same builder instance for fluent chaining.
   */
  withDebug(enabled: boolean): this {
    this.config.debug = enabled;
    return this;
  }

  /**
   * Enables or disables warnings for unresolved translation keys.
   *
   * @param enabled Whether missing-key warnings should be emitted.
   * @returns The same builder instance for fluent chaining.
   */
  withMissingKeyWarnings(enabled: boolean): this {
    this.config.missingKeyWarnings = enabled;
    return this;
  }

  /**
   * Sets the prefix applied to unresolved translation fallbacks.
   *
   * @param prefix Prefix added to missing-key fallback values.
   * @returns The same builder instance for fluent chaining.
   */
  withMissingKeyPrefix(prefix: string): this {
    this.config.missingKeyPrefix = prefix;
    return this;
  }

  /**
   * Finalizes and validates the current configuration draft.
   *
   * @returns A validated i18n runtime configuration object.
   * @throws {I18nConfigError} When the default or fallback locale is not present
   * in the supported locale list.
   */
  build(): I18nConfig {
    const fallbackLocale =
      this.config.fallbackLocale ?? this.config.defaultLocale;

    if (!this.config.supportedLocales.includes(this.config.defaultLocale)) {
      throw new I18nConfigError(
        `Default locale ${this.config.defaultLocale} must be in supported locales`,
        {
          defaultLocale: this.config.defaultLocale,
          supportedLocales: this.config.supportedLocales,
        },
      );
    }

    if (!this.config.supportedLocales.includes(fallbackLocale)) {
      throw new I18nConfigError(
        `Fallback locale ${fallbackLocale} must be in supported locales`,
        {
          fallbackLocale,
          supportedLocales: this.config.supportedLocales,
        },
      );
    }

    const finalConfig = {
      ...this.config,
      fallbackLocale,
    };

    const parseResult = i18nConfigSchema.safeParse(finalConfig);
    if (!parseResult.success) {
      throw new I18nConfigError("Invalid i18n configuration", {
        issues: parseResult.error.issues.map((issue) => ({
          path: issue.path.join("."),
          message: issue.message,
        })),
      });
    }

    return parseResult.data;
  }
}

/**
 * Creates a new i18n configuration builder.
 *
 * @returns A fluent `I18nConfigBuilder` instance.
 */
export function createI18nConfig(): I18nConfigBuilder {
  return new I18nConfigBuilder();
}

/**
 * Configuration for the shared translation loader.
 *
 * @property baseUrl Base URL used when fetching locale namespaces.
 * @property cache Enables in-memory namespace caching.
 * @property cacheDuration Cache duration in milliseconds.
 * @property preloadAll Makes preload() load every built-in supported locale.
 * @property onError Optional error fallback invoked when loading fails.
 */
export interface TranslationLoaderConfig {
  /** Base URL for loading translations */
  baseUrl?: string;
  /** Whether to cache loaded translations */
  cache?: boolean;
  /** Cache duration in milliseconds */
  cacheDuration?: number;
  /** Whether preload() should load every built-in supported locale */
  preloadAll?: boolean;
  /**
   * Error handler invoked when a translation fetch fails.
   * If the callback returns a TranslationDict, that value is used as the fallback.
   * If omitted, a {@link TranslationLoadError} is thrown.
   */
  onError?: (error: TranslationLoadError) => TranslationDict | undefined;
}

/**
 * Creates a translation loader.
 *
 * @param config Translation loader configuration overrides.
 * @returns A translation loader with load, preload, and cache helpers.
 *
 * @example
 * ```ts
 * const loader = createTranslationLoader({
 *   baseUrl: '/locales',
 *   cache: true,
 *   cacheDuration: 3600000
 * })
 *
 * const translations = await loader.load('en', 'common')
 * ```
 */
export function createTranslationLoader(
  config: TranslationLoaderConfig = {},
): TranslationLoader {
  const parsedConfig = translationLoaderConfigSchema.safeParse(config);
  if (!parsedConfig.success) {
    throw new I18nConfigError("Invalid translation loader configuration", {
      issues: parsedConfig.error.issues,
    });
  }

  const cache = new Map<string, { data: TranslationDict; timestamp: number }>();
  const inFlightLoads = new Map<string, Promise<TranslationDict>>();
  const {
    baseUrl = DEFAULT_TRANSLATIONS_BASE_URL,
    cache: enableCache = true,
    cacheDuration = DEFAULT_CACHE_DURATION_MS,
    preloadAll = false,
    onError,
  } = parsedConfig.data as TranslationLoaderConfig;

  const load = async (
    locale: Locale,
    namespace: I18nNamespace,
  ): Promise<TranslationDict> => {
    const request = normalizeLoaderRequest(locale, namespace);
    const cacheKey = `${request.locale}:${request.namespace}`;

    if (enableCache) {
      const cached = cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < cacheDuration) {
        return cached.data;
      }
    }

    const pendingLoad = inFlightLoads.get(cacheKey);
    if (pendingLoad) {
      return pendingLoad;
    }

    const loadPromise = (async (): Promise<TranslationDict> => {
      try {
        const url = buildNamespaceUrl(
          baseUrl,
          request.locale,
          request.namespace,
        );
        const response = await fetch(url);

        if (!response.ok) {
          const loadError = new TranslationLoadError(
            request.locale,
            request.namespace,
            new I18nError(
              `HTTP ${response.status}: ${response.statusText}`,
              "TRANSLATION_HTTP_ERROR",
              undefined,
              {
                locale: request.locale,
                namespace: request.namespace,
                status: response.status,
                statusText: response.statusText,
                url,
              },
            ),
          );
          if (onError) {
            const fallback = onError(loadError);
            return fallback ?? {};
          }
          throw loadError;
        }

        const data = await response.json();
        const payloadResult = boundedTranslationDictSchema.safeParse(data);
        if (!payloadResult.success) {
          const loadError = new TranslationLoadError(
            request.locale,
            request.namespace,
            new I18nError(
              "Translation payload must be a nested string dictionary",
              "INVALID_TRANSLATION_PAYLOAD",
              undefined,
              {
                locale: request.locale,
                namespace: request.namespace,
                url,
                issues: payloadResult.error.issues,
              },
            ),
          );
          if (onError) {
            const fallback = onError(loadError);
            return fallback ?? {};
          }
          throw loadError;
        }

        if (enableCache) {
          cache.set(cacheKey, {
            data: payloadResult.data,
            timestamp: Date.now(),
          });
        }

        return payloadResult.data;
      } catch (error) {
        if (error instanceof TranslationLoadError) {
          throw error;
        }
        const loadError = new TranslationLoadError(
          request.locale,
          request.namespace,
          error,
        );
        if (onError) {
          const fallback = onError(loadError);
          return fallback ?? {};
        }
        throw loadError;
      }
    })();

    inFlightLoads.set(cacheKey, loadPromise);

    try {
      return await loadPromise;
    } finally {
      inFlightLoads.delete(cacheKey);
    }
  };

  const preload = async (
    locales: Locale[],
    namespace: I18nNamespace,
  ): Promise<void> => {
    const targetLocales = preloadAll ? SUPPORTED_LOCALES : locales;
    await Promise.all(targetLocales.map((locale) => load(locale, namespace)));
  };

  return {
    load,

    /**
     * Preloads all locales for a namespace
     */
    preload,

    /**
     * Clears cache
     */
    clearCache(locale?: Locale, namespace?: I18nNamespace): void {
      if (locale && namespace) {
        cache.delete(`${locale}:${namespace}`);
      } else {
        cache.clear();
      }
    },

    /**
     * Gets cache stats
     */
    getCacheStats(): TranslationLoaderCacheStats {
      return {
        size: cache.size,
        entries: Array.from(cache.keys()),
      };
    },
  };
}

/**
 * Namespace loader for managing multiple translation namespaces.
 *
 * @param translationLoader Shared translation loader used to fetch namespaces.
 * @returns A namespace loader that can load, merge, and cache namespaces.
 *
 * @example
 * ```ts
 * const loader = createNamespaceLoader(createTranslationLoader())
 * const translations = await loader.loadMultiple('en', ['common', 'errors'])
 * ```
 */
export function createNamespaceLoader(
  translationLoader: TranslationLoader,
): NamespaceLoader {
  const namespaces = new Map<string, TranslationDict>();

  const load = async (
    locale: Locale,
    namespace: I18nNamespace,
  ): Promise<TranslationDict> => {
    const translations = await translationLoader.load(locale, namespace);
    const key = `${locale}:${namespace}`;
    namespaces.set(key, translations);
    return translations;
  };

  const loadMultiple = async (
    locale: Locale,
    namespaceList: I18nNamespace[],
  ): Promise<Record<string, TranslationDict>> => {
    const result: Record<string, TranslationDict> = {};

    for (const ns of namespaceList) {
      result[ns] = await load(locale, ns);
    }

    return result;
  };

  const get = (
    locale: Locale,
    namespace: I18nNamespace,
  ): TranslationDict | undefined => {
    return namespaces.get(`${locale}:${namespace}`);
  };

  const merge = (
    locale: Locale,
    namespaceList: I18nNamespace[],
  ): TranslationDict => {
    const merged: TranslationDict = {};

    for (const ns of namespaceList) {
      const trans = get(locale, ns);
      if (trans) {
        mergeTranslationDicts(merged, trans);
      }
    }

    return merged;
  };

  return {
    /**
     * Loads a single namespace
     */
    load,

    /**
     * Loads multiple namespaces
     */
    loadMultiple,

    /**
     * Gets loaded namespace
     */
    get,

    /**
     * Merges multiple translations into one
     */
    merge,
  };
}

/**
 * Default i18n configurations for common Geenius scenarios.
 */
export const i18nPresets = {
  /**
   * Simple single-language setup
   */
  simple: (locale: Locale = "en"): I18nConfig => {
    return createI18nConfig()
      .withDefaultLocale(locale)
      .withSupportedLocales([locale])
      .withFallbackLocale(locale)
      .build();
  },

  /**
   * Multi-language with common locales
   */
  multi: (defaultLocale: Locale = "en"): I18nConfig => {
    return createI18nConfig()
      .withDefaultLocale(defaultLocale)
      .withSupportedLocales(
        includeDefaultLocale(MULTI_PRESET_LOCALES, defaultLocale),
      )
      .build();
  },

  /**
   * Global localization
   */
  global: (defaultLocale: Locale = "en"): I18nConfig => {
    return createI18nConfig()
      .withDefaultLocale(defaultLocale)
      .withSupportedLocales([...SUPPORTED_LOCALES])
      .build();
  },

  /**
   * Development configuration with debug output
   */
  development: (): I18nConfig => {
    return createI18nConfig()
      .withDebug(true)
      .withMissingKeyWarnings(true)
      .withMissingKeyPrefix("[MISSING] ")
      .build();
  },
};
